export type Coords = { latitude: number; longitude: number; accuracy: number };

export function getPositionOnce(timeoutMs = 15_000): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Geolocation nicht verfügbar"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
          accuracy: p.coords.accuracy,
        }),
      (e) => reject(new Error(e.message || "GPS-Fehler")),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

export async function md5Base64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = md5(new Uint8Array(buf));
  let binary = "";
  for (const byte of digest) binary += String.fromCharCode(byte);
  return btoa(binary);
}

// Compact MD5 (public-domain reference implementation, adapted).
// weg.li requires a base64-encoded MD5 of the upload bytes.
function md5(bytes: Uint8Array): Uint8Array {
  const msgLen = bytes.length;
  const padLen = (msgLen + 8) % 64;
  const pad = 64 - (padLen === 0 ? 0 : padLen);
  const total = msgLen + pad + 8;
  const padded = new Uint8Array(total);
  padded.set(bytes);
  padded[msgLen] = 0x80;
  const bitLen = msgLen * 8;
  const view = new DataView(padded.buffer);
  view.setUint32(total - 8, bitLen >>> 0, true);
  view.setUint32(total - 4, Math.floor(bitLen / 0x100000000), true);

  let a0 = 0x67452301,
    b0 = 0xefcdab89,
    c0 = 0x98badcfe,
    d0 = 0x10325476;

  const K = new Uint32Array([
    0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
    0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
    0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
    0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
    0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
    0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
    0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
    0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
  ]);
  const S = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21];
  const numBlocks = total / 64;

  const rot = (x: number, n: number) => (x << n) | (x >>> (32 - n));
  const add = (x: number, y: number) => (x + y) | 0;

  for (let i = 0; i < numBlocks; i++) {
    const M = new Uint32Array(16);
    for (let j = 0; j < 16; j++) M[j] = view.getUint32(i * 64 + j * 4, true);
    let A = a0,
      B = b0,
      C = c0,
      D = d0;
    for (let k = 0; k < 64; k++) {
      let F: number,
        g: number,
        s: number;
      if (k < 16) {
        F = (B & C) | (~B & D);
        g = k;
        s = S[k & 3];
      } else if (k < 32) {
        F = (D & B) | (~D & C);
        g = (5 * k + 1) % 16;
        s = S[4 + (k & 3)];
      } else if (k < 48) {
        F = B ^ C ^ D;
        g = (3 * k + 5) % 16;
        s = S[8 + (k & 3)];
      } else {
        F = C ^ (B | ~D);
        g = (7 * k) % 16;
        s = S[12 + (k & 3)];
      }
      const temp = D;
      D = C;
      C = B;
      B = add(B, rot(add(add(A, F), add(K[k], M[g])) >>> 0, s));
      A = temp;
    }
    a0 = add(a0, A);
    b0 = add(b0, B);
    c0 = add(c0, C);
    d0 = add(d0, D);
  }

  const out = new Uint8Array(16);
  const outView = new DataView(out.buffer);
  outView.setUint32(0, a0 >>> 0, true);
  outView.setUint32(4, b0 >>> 0, true);
  outView.setUint32(8, c0 >>> 0, true);
  outView.setUint32(12, d0 >>> 0, true);
  return out;
}
